import { IAppContext } from 'App/AppContext';
import {
  apiCompleteDocumentConnectionGrant,
  apiStartDocumentConnectionGrant,
} from 'services/Api/documentProviderService';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';

/** The message type the API's callback page posts to the window that opened it. */
export const documentGrantMessageType = 'isoplanner-document-grant';

/** How long the popup may stay open before the attempt is given up as abandoned. */
const grantTimeoutMs = 10 * 60 * 1000;

export type DocumentGrantOutcome = 'connected' | 'cancelled' | 'blocked' | 'failed';

export interface IDocumentGrantResult {
  outcome: DocumentGrantOutcome;
  /** The provider's error word when it refused, for the toast. */
  error?: string;
}

interface IGrantMessage {
  type?: string;
  ok?: boolean;
  connectionId?: number;
  error?: string;
  /** The provider's code and the flow's state, when the provider granted: this window completes with them. */
  code?: string;
  state?: string;
}

/**
 * The origin the callback page runs on: the API's. Only a message from there is trusted, so a
 * page that happens to be open in another tab cannot pretend the grant succeeded.
 */
export const getDocumentGrantOrigin = (): string => {
  try {
    return new URL(Config.get('Api.BaseURL')).origin;
  } catch {
    return '';
  }
};

/** The redirect URI the admin registers at the provider; the API owns it, the client only shows it. */
export const getDocumentGrantRedirectUri = (): string => {
  return Config.get('Api.BaseURL').replace(/\/+$/, '') + '/documentconnections/grant/callback';
};

/**
 * Connect the signed-in user's own account to a user-delegated connection: the API hands out
 * the provider's authorization page, it opens in a popup, and the API's callback page hands
 * this window the provider's code through postMessage. This window, signed in, then completes
 * the grant at the API, which checks that the signed-in person is the one who started the flow
 * before it exchanges the code and stores the tokens; the anonymous callback stores nothing.
 * Resolves when the grant is completed, the popup reports a refusal, closes without reporting
 * (the person gave up), or could not be opened at all. Rejects with the API's refusal of the
 * start or the completion.
 *
 * The popup is opened BEFORE the API call: a window opened after an await is no longer inside
 * the click and most browsers block it. The blank window navigates once the url is known.
 */
export const connectDocumentConnectionAccount = async (
  appContext: IAppContext,
  connectionId: number,
): Promise<IDocumentGrantResult> => {
  const popup = window.open('', 'isoplanner-document-grant', 'popup=yes,width=560,height=720');

  if (!popup) {
    return { outcome: 'blocked' };
  }

  let url: string;

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    url = await apiStartDocumentConnectionGrant(connectionId, accessToken);
  } catch (err) {
    popup.close();
    throw err;
  }

  if (!url) {
    popup.close();

    return { outcome: 'failed' };
  }

  popup.location.href = url;

  const expectedOrigin = getDocumentGrantOrigin();

  return new Promise<IDocumentGrantResult>((resolve) => {
    let settled = false;

    const finish = (result: IDocumentGrantResult | Promise<IDocumentGrantResult>) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedTimer);
      window.clearTimeout(timeout);
      try {
        if (!popup.closed) popup.close();
      } catch {
        // a window on another origin may refuse; it closes itself anyway
      }
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      const data = event.data as IGrantMessage | undefined;
      if (!data || data.type !== documentGrantMessageType) return;
      if (data.connectionId !== undefined && data.connectionId !== connectionId) return;

      if (!data.ok || !data.code || !data.state) {
        finish({ outcome: 'failed', error: data.error });

        return;
      }

      // the provider granted: only this window, signed in as the person who started the flow,
      // turns the code into the grant
      finish(completeDocumentConnectionGrant(appContext, connectionId, data.code, data.state));
    };

    // the callback page closes itself after posting; a popup closed before that is the person giving up
    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        window.setTimeout(() => finish({ outcome: 'cancelled' }), 300);
      }
    }, 500);

    const timeout = window.setTimeout(() => finish({ outcome: 'cancelled' }), grantTimeoutMs);

    window.addEventListener('message', onMessage);
  });
};

const completeDocumentConnectionGrant = async (
  appContext: IAppContext,
  connectionId: number,
  code: string,
  state: string,
): Promise<IDocumentGrantResult> => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  await apiCompleteDocumentConnectionGrant(connectionId, code, state, accessToken);

  return { outcome: 'connected' };
};
